import store from '@/store'
import { hasRole, isNoMainMenuFromV4 } from 'utils/functions'
import { SESSION_WELCOME, SESSION_SKIP_WIZARD } from 'utils/constants'
import user from '@/services/user'
import { getV4Path, RouteName } from './config'
// views components
import Home from 'views/HomePage'
import LoginPage from 'views/LoginPage'

const routes = [
  {
    path: '/',
    name: 'Home',
    meta: {
      requiresAuth: true
    },
    component: Home,
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.Home, to))
        return false
      }
      const hasDashboardAccess = hasRole('ui', 'UI_HOME_DASHBOARD')
      const hasReportAccess =
        hasRole('report', 'ROLE_GET_REPORT_COSTCENTER') ||
        hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS') ||
        hasRole('report', 'ROLE_GET_REPORT_CHART') ||
        hasRole('report', 'ROLE_GET_REPORT_EMPLOYEE')
      const hasFinancialAccess =
        hasRole('billing', 'ROLE_GET_ORDER_PREVIEW_SUMMARY') &&
        hasRole('billing', 'ROLE_GET_ORDERS')

      if (!hasDashboardAccess && !hasReportAccess && !hasFinancialAccess) {
        user.logout()
        return
      }

      if (!hasDashboardAccess && !hasReportAccess && hasFinancialAccess) {
        return next({ name: 'Financial' })
      }

      if (!hasDashboardAccess && hasReportAccess) {
        return next({ name: 'Reports' })
      }

      next()
    }
  },
  {
    path: '/login',
    name: 'Login',
    meta: {
      noMainMenu: true
    },
    component: LoginPage,
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.Home, to))
        return false
      }
      return next()
    }
  },
  {
    path: '/primeiro-acesso/colaborador/:employeeId/token/:token',
    name: 'FirstAccess',
    meta: {
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'first-access' */ 'views/FirstAccessPage')
  },
  {
    path: '/bem-vindo',
    name: 'Welcome',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'welcome' */ 'views/Welcome'),
    beforeEnter (to, from, next) {
      if (!hasRole('costCenter', 'ROLE_POST_COSTCENTER') || localStorage.getItem(SESSION_WELCOME)) {
        return next({ name: 'Home' })
      }
      next()
    }
  },
  {
    path: '/wizard',
    name: 'Wizard',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'wizard' */ 'views/Wizard'),
    beforeEnter (to, from, next) {
      if (
        !hasRole('employee', 'ROLE_EMPLOYEE_WIZARD') ||
        localStorage.getItem(SESSION_SKIP_WIZARD)
      ) {
        return next({ name: 'Home' })
      }

      return next()
    }
  },
  {
    path: '/redefinir-senha/:token',
    name: 'ResetPassword',
    meta: {
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'reset-password-page' */ 'views/ResetPasswordPage')
  },
  {
    path: '/primeiro-acesso/:token',
    name: 'FirstAccessLogin',
    meta: {
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'reset-password-page' */ 'views/ResetPasswordPage')
  },
  {
    path: '/colaboradores/:id?',
    name: 'Employees',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'employees-page' */ 'views/EmployeesPage'),
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.Employees, to))
        return false
      }
      if (!hasRole('employee', 'ROLE_GET_EMPLOYEE')) {
        return next({ name: 'Home' })
      }

      if (to.params.id === 'cadastrar') {
        return next({
          name: 'Employee',
          params: { type: 'cadastrar' }
        })
      }

      return next()
    }
  },
  {
    path: '/usuarios',
    name: 'Users',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'users-page' */ 'views/UsersPage'),
    beforeEnter (to, from, next) {
      if (!hasRole('menu', 'ROLE_MENU_USERS')) {
        return next({ name: 'Home' })
      }

      return next()
    }
  },
  {
    path: '/usuario/:type/:id?',
    name: 'User',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'user-page' */ 'views/UserPage'),
    beforeEnter (to, from, next) {
      if (!hasRole('menu', 'ROLE_MENU_USERS')) {
        return next({ name: 'Home' })
      }

      return next()
    }
  },
  {
    path: '/colaborador/:type/:id?',
    name: 'Employee',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'employee-page' */ 'views/EmployeePage'),
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.Employee, to))
        return false
      }
      return !hasRole('employee', 'ROLE_GET_EMPLOYEE_ID') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/politica-de-credito-em-massa',
    name: 'EmployeeMassCreditPolicy',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(
        /* webpackChunkName: 'employee-mass-credit-policy-page' */ 'views/EmployeeMassCreditPolicyPage'
      ),
    beforeEnter (to, from, next) {
      return !hasRole('company', 'ROLE_POST_COMPANY_ID_EMPLOYEE_BATCHUPDATE')
        ? next({ name: 'Home' })
        : next()
    }
  },
  {
    path: '/configuracoes/empresa/:id/financeiro',
    name: 'CompanyFinancialConfig',
    meta: {
      requiresAuth: true,
      path: 'config',
      noMainMenu: true
    },
    component: () =>
      import(/* webpackChunkName: 'company-financial-config' */ 'views/CompanyFinancialConfigPage'),
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.CompanyFinancialConfig, to))
        return false
      }
      if (
        hasRole('profiles', 'ROLE_ADMINISTRATOR') ||
        hasRole('profiles', 'ROLE_FINANCIAL_ADMIN') ||
        hasRole('billing', 'ROLE_PUT_FINANCIAL_ACCOUNT_EMAILS_TO_NOTIFY')
      ) {
        return next()
      }

      return next({ name: 'Home' })
    }
  },
  {
    path: '/configuracoes/empresa/:id/detalhamento',
    name: 'CompanyDetail',
    meta: {
      requiresAuth: true,
      path: 'config'
    },
    component: () =>
      import(/* webpackChunkName: 'company-details-page' */ 'views/CompanyDetailsPage'),
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.CompanyDetail, to))
        return false
      }
      return !hasRole('company', 'ROLE_GET_COMPANY_ID') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/configuracoes/empresa/cadastrar/:id?',
    name: 'CreateCompany',
    meta: {
      requiresAuth: true,
      noMainMenu: true,
      path: 'config'
    },
    component: () => import(/* webpackChunkName: 'company-data-page' */ 'views/CompanyDataPage'),
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.CreateCompany, to))
        return false
      }
      return !hasRole('profiles', 'ROLE_ADMINISTRATOR') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/configuracoes/empresa/editar/:id?',
    name: 'EditCompany',
    meta: {
      requiresAuth: true,
      noMainMenu: true,
      path: 'config'
    },
    component: () => import(/* webpackChunkName: 'company-data-page' */ 'views/CompanyDataPage'),
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.EditCompany, to))
        return false
      }
      return hasRole('profiles', 'ROLE_ADMINISTRATOR') ||
        hasRole('company', 'ROLE_PUT_COMPANY_ID_PHONE')
        ? next()
        : next({ name: 'Home' })
    }
  },
  {
    path: '/configuracoes/empresa',
    name: 'Company',
    meta: {
      requiresAuth: true,
      path: 'config'
    },
    component: () => import(/* webpackChunkName: 'company-page' */ 'views/CompanyPage'),
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.Company, to))
        return false
      }
      const selectedCompany =
        store.state.company && store.state.company.selectedCompany
          ? store.state.company.selectedCompany
          : null

      if (!hasRole('company', 'ROLE_GET_COMPANY')) {
        return next({ name: 'Home' })
      }

      if (selectedCompany) {
        const { id } = selectedCompany
        return next({
          name: 'CompanyDetail',
          params: { id }
        })
      }

      next()
    }
  },
  {
    path: '/configuracoes/grupo-de-empresa/:type/:id?',
    name: 'CompanyGroup',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'company-group-page' */ 'views/CompanyGroupPage'),
    beforeEnter (to, from, next) {
      return !hasRole('profiles', 'ROLE_ADMINISTRATOR') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/configuracoes/centros-de-custo/:id?',
    name: 'CostCenters',
    meta: {
      requiresAuth: true,
      path: 'config'
    },
    component: () => import(/* webpackChunkName: 'cost-centers-page' */ 'views/CostCentersPage'),
    beforeEnter (to, from, next) {
      return !hasRole('costCenter', 'ROLE_GET_COSTCENTER') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/configuracoes/centro-de-custo/:type/:id?',
    name: 'CostCenter',
    meta: {
      requiresAuth: true,
      path: 'config',
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'cost-center-page' */ 'views/CostCenterPage'),
    beforeEnter (to, from, next) {
      return !hasRole('costCenter', 'ROLE_GET_COSTCENTER_ID') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/configuracoes/projetos/:id?/:type?',
    name: 'Projects',
    meta: {
      requiresAuth: true,
      path: 'config'
    },
    component: () => import(/* webpackChunkName: 'projects-page' */ 'views/ProjectsPage'),
    beforeEnter (to, from, next) {
      return !hasRole('project', 'ROLE_GET_PROJECT') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/configuracoes/politicas-de-uso',
    name: 'UsagePolicies',
    meta: {
      requiresAuth: true,
      path: 'config'
    },
    component: () =>
      import(/* webpackChunkName: 'usage-policies-page' */ 'views/UsagePoliciesPage'),
    beforeEnter (to, from, next) {
      if (!hasRole('timePolicy', 'ROLE_GET_TIMEPOLICY')) {
        return next({ name: 'Home' })
      }
      next()
    }
  },
  {
    path: '/configuracoes/politica-de-uso/:type/:id?',
    name: 'UsagePolicy',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'usage-policy-page' */ 'views/UsagePolicyPage'),
    beforeEnter (to, from, next) {
      return !hasRole('timePolicy', 'ROLE_GET_TIMEPOLICY_TIMEPOLICYID') ||
        !hasRole('timePolicy', 'ROLE_GET_TIMEPOLICY_TIMEPOLICYID_COSTCENTER')
        ? next({ name: 'Home' })
        : next()
    }
  },
  {
    path: '/configuracoes/politicas-de-credito',
    name: 'CreditPolicies',
    meta: {
      requiresAuth: true,
      path: 'config'
    },
    component: () =>
      import(/* webpackChunkName: 'credit-policies-page' */ 'views/CreditPoliciesPage'),
    beforeEnter (to, from, next) {
      return !hasRole('employee', 'ROLE_GET_EMPLOYEE_RIDECREDIT') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/configuracoes/incentivos',
    name: 'Incentives',
    meta: {
      requiresAuth: true,
      path: 'config'
    },
    component: () => import(/* webpackChunkName: 'incentives-page' */ 'views/IncentivesPage'),
    beforeEnter (to, from, next) {
      return !hasRole('billing', 'ROLE_GET_FINANCIAL_ACCOUNT_INCENTIVES') &&
        !hasRole('billing', 'ROLE_GET_INCENTIVES')
        ? next({ name: 'Home' })
        : next()
    }
  },
  {
    path: '/importacao-em-massa/incentivos/:id?',
    name: 'IncentivesBulkOperation',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(
        /* webpackChunkName: 'incentives-bulk-operation-page' */ 'views/IncentivesBulkOperationPage'
      ),
    beforeEnter (to, from, next) {
      return !hasRole('billing', 'ROLE_POST_INCENTIVE_UPLOAD') &&
        !hasRole('billing', 'ROLE_GET_INCENTIVE_UPLOAD') &&
        !hasRole('billing', 'ROLE_PATCH_INCENTIVE_PENDING_TO_APPROVAL')
        ? next({ name: 'Home' })
        : next()
    }
  },
  {
    path: '/relatorios/:type?',
    name: 'Reports',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'report-page' */ 'views/ReportsPage'),
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.Reports, to))
        return false
      }
      if (
        !hasRole('report', 'ROLE_GET_REPORT_COSTCENTER') &&
        !hasRole('report', 'ROLE_GET_REPORT_EMPLOYEE') &&
        !hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS')
      ) {
        return next({ name: 'Home' })
      }

      if (!to.params.type) {
        return next({
          name: 'Reports',
          params: { type: 'corridas' }
        })
      }
      return next()
    }
  },
  {
    path: '/relatorios-consolidados/:action?/:id?',
    name: 'ConsolidatedReports',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(/* webpackChunkName: 'consolidated-reports-page' */ 'views/ConsolidatedReportsPage'),
    beforeEnter (to, from, next) {
      if (
        !hasRole('report', 'ROLE_POST_REPORT_TAXIJOBS_SCHEDULE') ||
        !hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS_ACTIVES') ||
        !hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS_DOWNLOAD')
      ) {
        return next({ name: 'Home' })
      }

      return next()
    }
  },
  {
    path: '/relatorio-consolidado',
    name: 'ConsolidatedReport',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(/* webpackChunkName: 'consolidated-report-page' */ 'views/ConsolidatedReportPage'),
    beforeEnter (to, from, next) {
      if (
        !hasRole('report', 'ROLE_POST_REPORT_TAXIJOBS_SCHEDULE') ||
        !hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS_ACTIVES') ||
        !hasRole('report', 'ROLE_GET_REPORT_TAXIJOBS_DOWNLOAD')
      ) {
        return next({ name: 'Home' })
      }

      return next()
    }
  },
  {
    path: '/recibo-de-corridas',
    name: 'Receipts',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'receipt-page' */ 'views/ReceiptsPage'),
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.Receipts, to))
        return false
      }
      return !hasRole('report', 'ROLE_GET_REPORT_RECEIPT') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/financeiro/recobranca-manual',
    name: 'ManualRetrieval',
    meta: {
      requiresAuth: true,
      path: 'financial'
    },
    component: () => import(/* webpackChunkName: 'manual-retrieval' */ 'views/ManualRetrieval'),
    beforeEnter (to, from, next) {
      if (!hasRole('financial', 'ROLE_GET_ORDER_REPROCESS')) {
        return next({ name: 'Home' })
      }
      next()
    }
  },
  {
    path: '/financeiro/historico-de-faturas',
    name: 'Financial',
    meta: {
      requiresAuth: true,
      path: 'financial'
    },
    component: () => import(/* webpackChunkName: 'financial-page' */ 'components/newBilling'),
    beforeEnter (to, from, next) {
      if (
        !hasRole('billing', 'ROLE_GET_ORDER_PREVIEW_SUMMARY') &&
        !hasRole('billing', 'ROLE_GET_ORDERS')
      ) {
        return next({ name: 'Home' })
      }

      next()
    }
  },
  {
    path: '/financeiro/ajustes-financeiros',
    name: 'FinancialAdjustments',
    meta: {
      requiresAuth: true,
      path: 'financial'
    },
    component: () =>
      import(/* webpackChunkName: 'financial-adjustments-page' */ 'views/FinancialAdjustmentsPage'),
    beforeEnter (to, from, next) {
      if (hasRole('menu', 'ROLE_MENU_FINANCIAL_ADJUSTMENT')) {
        return next()
      }

      return next({ name: 'Home' })
    }
  },
  {
    path: '/financeiro/bg2-imortar-nfe',
    name: 'ImportNfe',
    meta: {
      requiresAuth: true,
      path: 'financial'
    },
    component: () => import(/* webpackChunkName: 'import-nfe-page' */ 'views/ImportNfePage'),
    beforeEnter (to, from, next) {
      return !hasRole('custom', 'ROLE_POST_PROCESS') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/financeiro/faturamento',
    name: 'Revenues',
    meta: {
      requiresAuth: true,
      path: 'financial'
    },
    component: () => import(/* webpackChunkName: 'revenues-page' */ 'views/RevenuesPage'),
    beforeEnter (to, from, next) {
      return !hasRole('financial', 'ROLE_GET_FINANCE_SUMMARY') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/financeiro/faturamento/importar-nfes',
    name: 'RevenuesNfeImport',
    meta: {
      requiresAuth: true,
      path: 'financial',
      noMainMenu: true
    },
    component: () =>
      import(/* webpackChunkName: 'revenues-nfe-import-page' */ 'views/RevenuesNfeImportPage'),
    beforeEnter (to, from, next) {
      return !hasRole('financial', 'ROLE_POST_ORDER_UPLOAD_NFE') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/financeiro/faturamento/historico',
    name: 'nfeImportHistory',
    meta: {
      requiresAuth: true,
      path: 'financial',
      noMainMenu: true
    },
    component: () =>
      import(/* webpackChunkName: 'nfe-import-history-page' */ 'views/NfeImportHistoryPage'),
    beforeEnter (to, from, next) {
      console.log('nfeImportHistory', hasRole('financial', 'ROLE_POST_ORDER_UPLOAD_NFE'))
      return !hasRole('financial', 'ROLE_POST_ORDER_UPLOAD_NFE') ? next({ name: 'Home' }) : next()
    }
  },
  {
    path: '/financeiro/credito-de-corrida/:type?/:id?',
    name: 'RideCredit',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'ride-credit-page' */ 'views/RideCreditPage'),
    beforeEnter (to, from, next) {
      return !hasRole('billing', 'ROLE_POST_CREATE_CREDIT') &&
        !hasRole('billing', 'ROLE_PUT_UPDATE_CREDIT')
        ? next({ name: 'Home' })
        : next()
    }
  },
  {
    path: '/financeiro/fatura/prorrogar-vencimento/:invoiceId?/:dueDateOpt?',
    name: 'InvoiceExtendDueDate',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(/* webpackChunkName: 'invoice-extend-due-date' */ 'views/InvoiceExtendDueDate'),
    beforeEnter (to, from, next) {
      return hasRole('billing', 'ROLE_POST_DUPLICATE_INVOICE') &&
        hasRole('billing', 'ROLE_GET_ORDER_SUMMARY_UPDATED')
        ? next()
        : next({ name: 'Home' })
    }
  },
  {
    path: '/financeiro/aprovar-pagamento',
    name: 'ApprovePayment',
    meta: {
      requiresAuth: true
    },
    component: () =>
      import(/* webpackChunkName: 'payment-status-page' */ 'views/ApprovePaymentPage'),
    beforeEnter (to, from, next) {
      console.log('approvePayment', '1')
      if (
        hasRole('billing', 'ROLE_GET_PAGINATED_ORDERS') ||
        hasRole('billing', 'ROLE_GET_MASSIVE_ORDER_PAYMENT_STATUS_WORKER')
      ) {
        return next()
      }
      console.log('approvePayment', '2')
      return next({ name: 'Home' })
    }
  },
  {
    path: '/financeiro/fatura/solicitacoes-esgotadas',
    name: 'InvoiceExtendDueDateSoldOutRequests',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(
        /* webpackChunkName: 'invoice-extend-due-date-soldout-requests' */ 'views/InvoiceExtendDueDateSoldOutRequests'
      ),
    beforeEnter (to, from, next) {
      return hasRole('billing', 'ROLE_POST_DUPLICATE_INVOICE') &&
        hasRole('billing', 'ROLE_GET_ORDER_SUMMARY_UPDATED')
        ? next()
        : next({ name: 'Home' })
    }
  },
  {
    path: '/financeiro/fatura/prorrogacao-expirada/:date',
    name: 'InvoiceExtendDueDateExpiredRequests',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(
        /* webpackChunkName: 'invoice-extend-due-date-expired-requests' */ 'views/InvoiceExtendDueDateSoldOutRequests'
      ),
    beforeEnter (to, from, next) {
      return hasRole('billing', 'ROLE_POST_DUPLICATE_INVOICE') &&
        hasRole('billing', 'ROLE_GET_ORDER_SUMMARY_UPDATED')
        ? next()
        : next({ name: 'Home' })
    }
  },
  {
    path: '/importacao-em-massa/aprovar-pagamento/:id?',
    name: 'ApprovePaymentBulkOperation',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(
        /* webpackChunkName: 'approve-payment-bulk-operation' */ 'views/ApprovePaymentBulkOperationPage'
      ),
    beforeEnter (to, from, next) {
      if (
        hasRole('billing', 'ROLE_POST_UPLOAD_MASSIVE_ORDER_FILE') &&
        hasRole('billing', 'ROLE_POST_RUN_MASSIVE_ORDER_PAYMENT_STATUS_WORKER')
      ) {
        return next()
      }

      next({ name: 'Home' })
    }
  },
  {
    path: '/buscar-telefone',
    name: 'SearchPhonePage',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'search-phone-page' */ 'views/SearchPhonePage'),
    beforeEnter (to, from, next) {
      if (isNoMainMenuFromV4()) {
        window.navigateToReact(getV4Path(RouteName.SearchPhonePage, to))
        return false
      }
      if (!hasRole('menu', 'ROLE_MENU_CELPHONE_SEARCH')) {
        return next({ name: 'Home' })
      }

      return next()
    }
  },
  {
    path: '/perfis',
    name: 'AccessProfilesPage',
    meta: {
      requiresAuth: true
    },
    component: () =>
      import(/* webpackChunkName: 'access-profiles-page' */ 'views/AccessProfilesPage')
  },
  {
    path: '/perfis/:id?',
    name: 'AccessProfilesDetailsPage',
    meta: {
      requiresAuth: true
    },
    component: () =>
      import(
        /* webpackChunkName: 'access-profiles-details-page' */ 'views/AccessProfilesDetailsPage'
      )
  },
  {
    path: '/nova-chamada-web',
    name: 'WebCall',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'new-webcall-page' */ 'views/NewWebCallPage'),
    beforeEnter (to, from, next) {
      if (
        !hasRole('profiles', 'ROLE_ADMINISTRATOR') &&
        !hasRole('custom', 'WEBCALL_PERMISSION') &&
        !hasRole('custom', 'THIRDPARTY_WEBCALL_PERMISSION')
      ) {
        return next({ name: 'Home' })
      }
      next()
    }
  },
  {
    path: '/chamada-web',
    name: 'WebCall_old',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'new-webcall-page' */ 'views/NewWebCallPage'),
    beforeEnter (to, from, next) {
      if (
        !hasRole('profiles', 'ROLE_ADMINISTRATOR') &&
        !hasRole('custom', 'WEBCALL_PERMISSION') &&
        !hasRole('custom', 'THIRDPARTY_WEBCALL_PERMISSION')
      ) {
        return next({ name: 'Home' })
      }
      next()
    }
  },
  {
    path: '/importacao-em-massa/centro-de-custo/:id?',
    name: 'CostCenterBulkOperation',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(
        /* webpackChunkName: 'costcenter-bulk-operation' */ 'views/CostCenterBulkOperationPage'
      ),
    beforeEnter (to, from, next) {
      if (
        hasRole('company', 'ROLE_POST_COMPANY_ID_COSTCENTER_UPLOAD') &&
        hasRole('company', 'ROLE_POST_COMPANY_COMPANYID_BULKOPERATION_ID_EXECUTE')
      ) {
        return next()
      }

      next({ name: 'Home' })
    }
  },
  {
    path: '/importacao-em-massa/colaboradores/:id?',
    name: 'EmployeeBulkOperation',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(/* webpackChunkName: 'employee-bulk-operation' */ 'views/EmployeeBulkOperationPage'),
    beforeEnter (to, from, next) {
      if (
        hasRole('company', 'ROLE_POST_COMPANY_ID_EMPLOYEE_UPLOAD') &&
        hasRole('company', 'ROLE_POST_COMPANY_COMPANYID_BULKOPERATION_ID_EXECUTE')
      ) {
        return next()
      }

      next({ name: 'Home' })
    }
  },
  {
    path: '/importacao-em-massa/projetos/:id?',
    name: 'ProjectBulkOperation',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () =>
      import(/* webpackChunkName: 'project-bulk-operation' */ 'views/ProjectBulkOperationPage'),
    beforeEnter (to, from, next) {
      if (
        hasRole('company', 'ROLE_POST_COMPANY_ID_PROJECT_UPLOAD') &&
        hasRole('company', 'ROLE_POST_COMPANY_COMPANYID_BULKOPERATION_ID_EXECUTE')
      ) {
        return next()
      }

      next({ name: 'Home' })
    }
  },
  {
    path: '/solicitar-token',
    name: 'TokenRequest',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'token-request' */ 'views/TokenRequestPage'),
    beforeEnter (to, from, next) {
      if (!hasRole('custom', 'ROLE_POST_REQUEST_API_TOKEN')) {
        return next({ name: 'Home' })
      }
      next()
    }
  },
  {
    path: '/99cupons' /** 这个页面已经废弃 */,
    name: 'BuyRideCoupon',
    meta: {
      requiresAuth: true,
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'buy-ride-coupon' */ 'views/BuyRideCouponPage'),
    beforeEnter (to, from, next) {
      const companyPlan =
        store.state.company.selectedCompany && store.state.company.selectedCompany.plan
          ? store.state.company.selectedCompany.plan.toUpperCase()
          : null

      if (companyPlan === 'KA' && hasRole('ui', 'UI_VIEW_COUPON_REQUEST')) {
        return next()
      }

      return next({ name: 'Home' })
    }
  },
  {
    path: '/debug',
    name: 'Debug',
    meta: {
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'debug-page' */ 'views/Debug')
  },
  {
    path: '/sistema-indisponivel',
    name: 'UnavailablePage',
    component: () => import(/* webpackChunkName: 'unavailable-page' */ 'views/UnavailablePage')
  },
  {
    path: '/link-expirado',
    name: 'ExpiredLink',
    meta: {
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'expired-link-page' */ 'views/ExpiredLinkPage')
  },
  // {
  //   path: '/sap-concur-integration', /* 这个路由已经不使用，关闭 */
  //   name: 'SAPConcurIntegration',
  //   meta: {
  //     noMainMenu: true
  //   },
  //   component: () => import(/* webpackChunkName: 'sap-concur-integration-page' */ 'views/SAPConcurIntegrationPage')
  // },
  {
    path: '/verificar-link',
    name: 'CheckLink',
    meta: {
      noMainMenu: true
    },
    component: () => import(/* webpackChunkName: 'check-link-page' */ 'views/CheckLinkPage')
  },
  {
    path: '/highcharts',
    name: 'Highcharts',
    meta: {
      noMainMenu: true,
      requiresAuth: true
    },
    component: () => import(/* webpackChunkName: 'ride-credit-page' */ 'views/HighchartsPage')
  },
  // 闪送
  {
    path: '/entregaCall',
    name: 'EntregaCall',
    meta: {
      requiresAuth: true,
      hideChat: true
    },
    component: () => import(/* webpackChunkName: 'employees-page' */ 'views/EntregaCall')
  },
  // 闪送订单
  {
    path: '/entregaOrders',
    name: 'EntregaOrders',
    meta: {
      requiresAuth: true,
      hideChat: true
    },
    component: () => import(/* webpackChunkName: 'employees-page' */ 'views/EntregaOrder')
  },
  // otherwise redirect to home -- we can improve to a 404 page
  {
    path: '*',
    redirect: '/'
  }
]

export default routes
